import { prefersReducedMotion } from '@theme/utilities';

/* -------------------------------------------------------------------------
 * The email form is intentionally plain `{% form 'customer' %}` with no JS.
 * Shopify's default redirect-to-referrer lands the visitor back on this page
 * after processing, with form.posted_successfully?/form.errors already
 * correctly rendered.
 * ---------------------------------------------------------------------- */

/* -------------------------------------------------------------------------
 * Shared WebGL2 helpers for the shader effects below. Both effects are
 * decorative enhancements layered on top of already-functional <img>
 * elements, so any failure here (no WebGL2, shader compile error,
 * CORS-blocked texture) is caught and swallowed by the callers.
 * ---------------------------------------------------------------------- */

const QUAD_VERTEX_SOURCE = `#version 300 es
  in vec2 a_position;
  out vec2 v_uv;
  void main() {
    v_uv = a_position * 0.5 + 0.5;
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

/**
 * @param {WebGL2RenderingContext} gl
 * @param {number} type
 * @param {string} source
 */
function compileShader(gl, type, source) {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('Unable to create shader');

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile error: ${info}`);
  }

  return shader;
}

/**
 * Compiles the shared fullscreen-quad vertex shader against a fragment shader,
 * links the program, and binds a [-1,1] quad to attribute location 0.
 * @param {WebGL2RenderingContext} gl
 * @param {string} fragmentSource
 */
function createQuadProgram(gl, fragmentSource) {
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, QUAD_VERTEX_SOURCE);
  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);

  const program = gl.createProgram();
  if (!program) throw new Error('Unable to create program');

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.bindAttribLocation(program, 0, 'a_position');
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Program link error: ${info}`);
  }

  const quadBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

  return program;
}

/**
 * @param {WebGL2RenderingContext} gl
 * @param {TexImageSource} source
 */
function createTexture(gl, source) {
  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  return texture;
}

/**
 * Resolves once an already-in-the-page <img> has finished loading, so it can be
 * used directly as a WebGL texture source — no second network request. Requires
 * the <img> to carry `crossorigin="anonymous"` (set in the section markup); if the
 * server doesn't answer with matching CORS headers, the later texImage2D call
 * throws and the caller's try/catch simply skips the effect.
 * @param {HTMLImageElement} img
 * @returns {Promise<HTMLImageElement>}
 */
function waitForImageReady(img) {
  if (img.complete && img.naturalWidth > 0) return Promise.resolve(img);

  return new Promise((resolve, reject) => {
    img.addEventListener('load', () => resolve(img), { once: true });
    img.addEventListener('error', reject, { once: true });
  });
}

/**
 * Forces a <picture>'s child <img> to re-resolve which <source> currently
 * applies. Browsers don't reliably do this on their own when the viewport
 * crosses a <source> media breakpoint on an already-loaded page — only on the
 * initial resolve. Removing and restoring src *and* srcset (with a layout
 * flush in between, so the removal isn't coalesced away) forces a genuine
 * re-resolve; srcset matters for image_picker images specifically, which
 * render through image_tag's srcset/sizes output rather than a plain src.
 * @param {HTMLImageElement} img
 */
function forceImageResolve(img) {
  const src = img.getAttribute('src');
  const srcset = img.getAttribute('srcset');
  if (!src && !srcset) return;

  if (src) img.removeAttribute('src');
  if (srcset) img.removeAttribute('srcset');
  void img.offsetWidth;
  if (srcset) img.setAttribute('srcset', srcset);
  if (src) img.setAttribute('src', src);
}

/**
 * Maps a viewport point to normalized [0,1] UV coordinates on media rendered with
 * `object-fit: cover` inside `rect` — i.e. where on the source image a click landed,
 * accounting for whatever cover-cropping is currently hiding part of it.
 * @param {number} clientX
 * @param {number} clientY
 * @param {DOMRect} rect
 * @param {number} mediaWidth
 * @param {number} mediaHeight
 */
function coverUV(clientX, clientY, rect, mediaWidth, mediaHeight) {
  const containerAspect = rect.width / rect.height;
  const mediaAspect = mediaWidth / mediaHeight;

  let scale;
  let offsetX = 0;
  let offsetY = 0;

  if (containerAspect > mediaAspect) {
    scale = rect.width / mediaWidth;
    offsetY = (mediaHeight * scale - rect.height) / 2 / scale;
  } else {
    scale = rect.height / mediaHeight;
    offsetX = (mediaWidth * scale - rect.width) / 2 / scale;
  }

  const u = (offsetX + (clientX - rect.left) / scale) / mediaWidth;
  const v = (offsetY + (clientY - rect.top) / scale) / mediaHeight;

  return [u, v];
}

/** Caps a raster's internal render resolution while preserving aspect ratio. */
function capResolution(width, height, maxWidth) {
  if (width <= maxWidth) return [width, height];
  const scale = maxWidth / width;
  return [Math.round(width * scale), Math.round(height * scale)];
}

/* -------------------------------------------------------------------------
 * Desktop: a chromatic-aberration warp over the hero image. Mouse/trackpad
 * visitors (matchMedia 'hover: hover and pointer: fine') get a lens that
 * follows the cursor continuously, only while actually hovering; everyone
 * else (touch — phones and tablets alike) gets the click-triggered,
 * self-decaying ripple, since there's no real "hover" to hook into there and
 * a tap already fires a click.
 * ---------------------------------------------------------------------- */

const RIPPLE_FRAGMENT_SOURCE = `#version 300 es
  precision highp float;

  in vec2 v_uv;
  uniform sampler2D u_texture;
  uniform vec2 u_origin;
  uniform vec2 u_aspect;
  uniform float u_time;
  uniform float u_duration;
  out vec4 outColor;

  void main() {
    vec2 uv = v_uv;
    vec2 diff = (uv - u_origin) * u_aspect;
    float dist = length(diff);
    vec2 dir = dist > 0.0001 ? diff / dist : vec2(0.0);

    // Eased (not linear) so the last stretch of the fade tapers off instead of
    // getting cut at a constant rate right up to the moment the canvas hides.
    float t = clamp(u_time / u_duration, 0.0, 1.0);
    float envelope = 1.0 - smoothstep(0.0, 1.0, t);
    float wavefront = u_time * 1.1;
    float ring = smoothstep(0.16, 0.0, abs(dist - wavefront)) * envelope;

    vec2 offset = dir * ring * 0.045 / u_aspect;
    float aberration = ring * 0.014;
    vec2 aberrOffset = dir * aberration / u_aspect;

    float r = texture(u_texture, uv + offset + aberrOffset).r;
    float g = texture(u_texture, uv + offset).g;
    float b = texture(u_texture, uv + offset - aberrOffset).b;

    outColor = vec4(r, g, b, 1.0);
  }
`;

const HOVER_FRAGMENT_SOURCE = `#version 300 es
  precision highp float;

  in vec2 v_uv;
  uniform sampler2D u_texture;
  uniform vec2 u_origin;
  uniform vec2 u_aspect;
  uniform float u_strength;
  out vec4 outColor;

  void main() {
    vec2 uv = v_uv;
    vec2 diff = (uv - u_origin) * u_aspect;
    float dist = length(diff);
    vec2 dir = dist > 0.0001 ? diff / dist : vec2(0.0);

    // Fixed-radius lens centered on the live cursor position — u_strength eases
    // 0<->1 in JS on enter/leave so it fades in and out rather than snapping.
    float falloff = smoothstep(0.3, 0.0, dist) * u_strength;
    vec2 offset = dir * falloff * -0.03 / u_aspect;
    float aberration = falloff * 0.01;
    vec2 aberrOffset = dir * aberration / u_aspect;

    float r = texture(u_texture, uv + offset + aberrOffset).r;
    float g = texture(u_texture, uv + offset).g;
    float b = texture(u_texture, uv + offset - aberrOffset).b;

    outColor = vec4(r, g, b, 1.0);
  }
`;

/**
 * Wraps the desktop hero image with a chromatic-aberration warp effect.
 * Isolated in its own custom element per the section's documented extension
 * point — no restructuring needed to add this.
 *
 * On hover-capable devices, a click always wins over the ambient hover lens:
 * it clears the lens immediately and plays the ring ripple instead, and the
 * lens stays off afterward until the mouse actually moves again (not just
 * on a timer) — so a click reads as a deliberate, separate gesture rather
 * than getting blended into whatever the lens was doing.
 */
class PasswordHeroRipple extends HTMLElement {
  static RIPPLE_DURATION = 1.5;
  /** How quickly u_strength eases toward its target each frame, on hover devices. */
  static HOVER_EASE = 0.15;
  /** Matches the section's max-width: 749px breakpoint for the mobile layout. */
  static BREAKPOINT_QUERY = '(min-width: 750px)';

  connectedCallback() {
    if (prefersReducedMotion()) return;

    this.#hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    // Deferred until the breakpoint this element is shown at is confirmed
    // active (and torn down if it stops being active), so setup never reads
    // the <img> while its <picture> is resolved to the cross-breakpoint
    // placeholder (see the section markup).
    this.#breakpointQuery = window.matchMedia(PasswordHeroRipple.BREAKPOINT_QUERY);
    this.#breakpointQuery.addEventListener('change', this.#handleBreakpointChange);

    if (this.#breakpointQuery.matches) this.#activate();
  }

  disconnectedCallback() {
    this.#breakpointQuery?.removeEventListener('change', this.#handleBreakpointChange);
    this.#deactivate();
  }

  #handleBreakpointChange = () => {
    if (this.#breakpointQuery?.matches) {
      // Runs every crossing, independent of the one-time WebGL setup below.
      const image = this.querySelector('img');
      if (image instanceof HTMLImageElement) forceImageResolve(image);
      this.#activate();
    } else {
      this.#deactivate();
    }
  };

  #hoverCapable = false;
  #activated = false;
  /** @type {MediaQueryList | null} */
  #breakpointQuery = null;
  /** @type {WebGL2RenderingContext | null} */
  #gl = null;
  /** @type {HTMLCanvasElement | null} */
  #canvas = null;
  /** @type {WebGLProgram | null} */
  #hoverProgram = null;
  /** @type {WebGLProgram | null} */
  #rippleProgram = null;
  /** @type {Record<string, WebGLUniformLocation | null>} */
  #hoverUniforms = {};
  /** @type {Record<string, WebGLUniformLocation | null>} */
  #rippleUniforms = {};
  /** @type {number | undefined} */
  #rafId;
  #startTime = 0;
  #origin = [0.5, 0.5];
  #hoverTarget = 0;
  #hoverCurrent = 0;
  /** @type {'idle' | 'hovering' | 'rippling'} */
  #mode = 'idle';

  #activate() {
    if (this.#activated) return;

    const image = this.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return;

    this.#activated = true;

    if (this.#hoverCapable) {
      this.addEventListener('mouseenter', this.#handleMouseEnter);
      this.addEventListener('mousemove', this.#handleMouseMove);
      this.addEventListener('mouseleave', this.#handleMouseLeave);
    } else {
      this.style.cursor = 'pointer';
    }

    this.addEventListener('click', this.#handleClick);
    this.#setup(image);
  }

  /** Permanent for this page view — only a fresh page load re-activates. */
  #deactivate() {
    if (!this.#activated) return;
    this.#activated = false;

    this.removeEventListener('mouseenter', this.#handleMouseEnter);
    this.removeEventListener('mousemove', this.#handleMouseMove);
    this.removeEventListener('mouseleave', this.#handleMouseLeave);
    this.removeEventListener('click', this.#handleClick);

    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#rafId = undefined;
    this.#canvas?.remove();
    this.#canvas = null;
    this.#gl = null;
    this.#mode = 'idle';
    this.#hoverTarget = 0;
    this.#hoverCurrent = 0;
  }

  /** @param {HTMLImageElement} image */
  async #setup(image) {
    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    const gl = canvas.getContext('webgl2', { premultipliedAlpha: false });
    if (!gl) return;

    try {
      await waitForImageReady(image);

      // Bail if the breakpoint changed while the image was still loading —
      // this.querySelector('img') could now be a different picture branch.
      if (!this.#activated) return;

      // Sized to the image's own aspect ratio, not the container's — the canvas
      // relies on CSS object-fit: cover to crop it exactly like the <img> it
      // sits over, which only lines up if the drawing buffer's aspect ratio
      // matches the photo's.
      const [w, h] = capResolution(image.naturalWidth, image.naturalHeight, 1600);
      canvas.width = w;
      canvas.height = h;

      // Uploaded once; both programs read it from texture unit 0.
      createTexture(gl, image);

      const rippleProgram = createQuadProgram(gl, RIPPLE_FRAGMENT_SOURCE);
      gl.useProgram(rippleProgram);
      this.#rippleUniforms = {
        texture: gl.getUniformLocation(rippleProgram, 'u_texture'),
        origin: gl.getUniformLocation(rippleProgram, 'u_origin'),
        aspect: gl.getUniformLocation(rippleProgram, 'u_aspect'),
        time: gl.getUniformLocation(rippleProgram, 'u_time'),
        duration: gl.getUniformLocation(rippleProgram, 'u_duration'),
      };
      gl.uniform1i(this.#rippleUniforms.texture, 0);
      this.#rippleProgram = rippleProgram;

      if (this.#hoverCapable) {
        const hoverProgram = createQuadProgram(gl, HOVER_FRAGMENT_SOURCE);
        gl.useProgram(hoverProgram);
        this.#hoverUniforms = {
          texture: gl.getUniformLocation(hoverProgram, 'u_texture'),
          origin: gl.getUniformLocation(hoverProgram, 'u_origin'),
          aspect: gl.getUniformLocation(hoverProgram, 'u_aspect'),
          strength: gl.getUniformLocation(hoverProgram, 'u_strength'),
        };
        gl.uniform1i(this.#hoverUniforms.texture, 0);
        this.#hoverProgram = hoverProgram;
      }

      this.#gl = gl;
      this.#canvas = canvas;
      this.appendChild(canvas);
    } catch {
      // No WebGL2, CORS-blocked texture, or shader compile failure — the plain
      // <img> underneath is already the complete, correct experience.
    }
  }

  /** @param {MouseEvent} event */
  #updateOrigin(event) {
    if (!this.#canvas) return;
    this.#origin = coverUV(event.clientX, event.clientY, this.getBoundingClientRect(), this.#canvas.width, this.#canvas.height);
  }

  #enterHover() {
    if (this.#mode === 'rippling' || !this.#canvas) return;
    this.#mode = 'hovering';
    this.#hoverTarget = 1;
    this.#canvas.classList.add('is-visible');
    if (!this.#rafId) this.#rafId = requestAnimationFrame(this.#renderHover);
  }

  /** @param {MouseEvent} event */
  #handleMouseEnter = (event) => {
    if (!this.#gl) return;
    this.#updateOrigin(event);
    this.#enterHover();
  };

  /** @param {MouseEvent} event */
  #handleMouseMove = (event) => {
    if (!this.#gl) return;
    this.#updateOrigin(event);
    // A click clears hovering down to 'idle'; only a fresh mousemove — not a
    // timer — should bring the lens back.
    if (this.#mode === 'idle') this.#enterHover();
  };

  #handleMouseLeave = () => {
    if (this.#mode === 'hovering') this.#hoverTarget = 0;
  };

  #renderHover = () => {
    const gl = this.#gl;
    const canvas = this.#canvas;
    if (!gl || !canvas || this.#mode !== 'hovering') {
      this.#rafId = undefined;
      return;
    }

    this.#hoverCurrent += (this.#hoverTarget - this.#hoverCurrent) * PasswordHeroRipple.HOVER_EASE;

    if (this.#hoverTarget === 0 && this.#hoverCurrent < 0.01) {
      this.#hoverCurrent = 0;
      canvas.classList.remove('is-visible');
      this.#mode = 'idle';
      this.#rafId = undefined;
      return;
    }

    gl.useProgram(this.#hoverProgram);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform2f(this.#hoverUniforms.origin, this.#origin[0], this.#origin[1]);
    gl.uniform2f(this.#hoverUniforms.aspect, canvas.width / canvas.height, 1);
    gl.uniform1f(this.#hoverUniforms.strength, this.#hoverCurrent);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    this.#rafId = requestAnimationFrame(this.#renderHover);
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!this.#gl || !this.#canvas) return;

    if (this.#hoverCapable) {
      this.#mode = 'rippling';
      this.#hoverTarget = 0;
      this.#hoverCurrent = 0;
    }

    this.#origin = coverUV(event.clientX, event.clientY, this.getBoundingClientRect(), this.#canvas.width, this.#canvas.height);
    this.#startTime = performance.now();
    this.#canvas.classList.add('is-visible');

    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(this.#renderRipple);
  };

  #renderRipple = () => {
    const gl = this.#gl;
    const canvas = this.#canvas;
    if (!gl || !canvas) return;

    const elapsed = (performance.now() - this.#startTime) / 1000;

    if (elapsed >= PasswordHeroRipple.RIPPLE_DURATION) {
      canvas.classList.remove('is-visible');
      if (this.#hoverCapable) this.#mode = 'idle';
      this.#rafId = undefined;
      return;
    }

    gl.useProgram(this.#rippleProgram);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform2f(this.#rippleUniforms.origin, this.#origin[0], this.#origin[1]);
    gl.uniform2f(this.#rippleUniforms.aspect, canvas.width / canvas.height, 1);
    gl.uniform1f(this.#rippleUniforms.time, elapsed);
    gl.uniform1f(this.#rippleUniforms.duration, PasswordHeroRipple.RIPPLE_DURATION);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    this.#rafId = requestAnimationFrame(this.#renderRipple);
  };
}

if (!customElements.get('password-hero-ripple')) {
  customElements.define('password-hero-ripple', PasswordHeroRipple);
}

/* -------------------------------------------------------------------------
 * Mobile: glitch-dissolve transition between cycling images, replacing the
 * plain CSS crossfade whenever WebGL2 is available.
 * ---------------------------------------------------------------------- */

const DISSOLVE_FRAGMENT_SOURCE = `#version 300 es
  precision highp float;

  in vec2 v_uv;
  uniform sampler2D u_textureA;
  uniform sampler2D u_textureB;
  uniform float u_progress;
  out vec4 outColor;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  void main() {
    vec2 uv = v_uv;

    vec2 cell = floor(uv * 40.0);
    float n = hash(cell);
    float edge = 0.1;
    float threshold = smoothstep(u_progress - edge, u_progress + edge, n);
    float wave = smoothstep(0.0, 1.0, 1.0 - abs(n - u_progress) / edge);

    float glitch = (hash(vec2(floor(uv.y * 60.0), u_progress)) - 0.5) * 0.05 * wave;
    vec2 uvA = uv + vec2(glitch, 0.0);
    vec2 uvB = uv - vec2(glitch, 0.0);

    float aberr = wave * 0.01;
    vec4 colA = vec4(
      texture(u_textureA, uvA + vec2(aberr, 0.0)).r,
      texture(u_textureA, uvA).g,
      texture(u_textureA, uvA - vec2(aberr, 0.0)).b,
      1.0
    );
    vec4 colB = vec4(
      texture(u_textureB, uvB + vec2(aberr, 0.0)).r,
      texture(u_textureB, uvB).g,
      texture(u_textureB, uvB - vec2(aberr, 0.0)).b,
      1.0
    );

    outColor = mix(colB, colA, threshold);
  }
`;

/**
 * Auto-cycles between stacked <img> layers. When WebGL2 is available, each
 * advance renders a glitchy, chromatic-aberration dissolve between the outgoing
 * and incoming images instead of a plain CSS opacity crossfade; the CSS
 * crossfade (see the stylesheet) remains the fallback while textures are still
 * loading and on any WebGL failure. Pauses when the document is hidden or the
 * element scrolls out of view, and does nothing at all (freezing on the first,
 * server-rendered `is-active` image) when the visitor prefers reduced motion.
 */
class PasswordHeroMobileCycle extends HTMLElement {
  /** Matches the section's max-width: 749px breakpoint for the mobile layout. */
  static BREAKPOINT_QUERY = '(max-width: 749px)';

  connectedCallback() {
    this.#images = Array.from(this.querySelectorAll('img'));

    if (this.#images.length < 2 || prefersReducedMotion()) return;

    // WebGL setup is deferred/torn down the same way as
    // PasswordHeroRipple#connectedCallback, for the same reason. The
    // auto-cycle timer itself doesn't need this guard, since it already only
    // runs while intersecting (never true while display: none).
    this.#breakpointQuery = window.matchMedia(PasswordHeroMobileCycle.BREAKPOINT_QUERY);
    this.#breakpointQuery.addEventListener('change', this.#handleBreakpointChange);
    if (this.#breakpointQuery.matches) this.#setupTransition();

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#play();
          } else {
            this.#pause();
          }
        }
      },
      { threshold: 0.2 }
    );
    this.#observer.observe(this);

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  disconnectedCallback() {
    this.#pause();
    this.#observer?.disconnect();
    this.#breakpointQuery?.removeEventListener('change', this.#handleBreakpointChange);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  #handleBreakpointChange = () => {
    if (this.#breakpointQuery?.matches) {
      // Only images[0] (mobile image 1) is picture-guarded against the
      // desktop breakpoint; 2 and 3 are loading="lazy" and load correctly
      // once visible regardless.
      const first = this.#images[0];
      if (first instanceof HTMLImageElement) forceImageResolve(first);
      this.#setupTransition();
    } else {
      this.#teardownTransition();
    }
  };

  /** @type {HTMLImageElement[]} */
  #images = [];
  #index = 0;
  /** @type {number | undefined} */
  #interval;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {MediaQueryList | null} */
  #breakpointQuery = null;
  #transitionActivated = false;

  /** @type {WebGL2RenderingContext | null} */
  #gl = null;
  /** @type {WebGLProgram | null} */
  #program = null;
  /** @type {HTMLCanvasElement | null} */
  #canvas = null;
  /** @type {WebGLTexture[]} */
  #textures = [];
  /** @type {Record<string, WebGLUniformLocation | null>} */
  #uniforms = {};
  /** @type {number | undefined} */
  #rafId;

  async #setupTransition() {
    if (this.#transitionActivated) return;
    this.#transitionActivated = true;

    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    const gl = canvas.getContext('webgl2', { premultipliedAlpha: false });
    if (!gl) return;

    try {
      await Promise.all(this.#images.map(waitForImageReady));

      // Bail if the breakpoint changed while images were still loading.
      if (!this.#transitionActivated) return;

      // Sized to the images' own aspect ratio (all three share one), not the
      // container box — see the matching comment in PasswordHeroRipple#setup.
      const first = this.#images[0];
      const [w, h] = capResolution(first.naturalWidth, first.naturalHeight, 1600);
      canvas.width = w;
      canvas.height = h;

      const program = createQuadProgram(gl, DISSOLVE_FRAGMENT_SOURCE);
      const textures = this.#images.map((img) => createTexture(gl, img));
      gl.useProgram(program);

      this.#uniforms = {
        textureA: gl.getUniformLocation(program, 'u_textureA'),
        textureB: gl.getUniformLocation(program, 'u_textureB'),
        progress: gl.getUniformLocation(program, 'u_progress'),
      };

      this.#gl = gl;
      this.#program = program;
      this.#canvas = canvas;
      this.#textures = textures;
      this.appendChild(canvas);
    } catch {
      // Falls back to the plain CSS crossfade already handled by #advance().
    }
  }

  /** Permanent for this page view — only a fresh page load re-activates. */
  #teardownTransition() {
    if (!this.#transitionActivated) return;
    this.#transitionActivated = false;

    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#rafId = undefined;
    this.#canvas?.remove();
    this.#canvas = null;
    this.#gl = null;
    this.#program = null;
    this.#textures = [];
  }

  #play() {
    if (this.#interval || document.hidden) return;

    const seconds = parseFloat(this.dataset.displaySeconds ?? '') || 4;
    this.#interval = window.setInterval(() => this.#advance(), seconds * 1000);
  }

  #pause() {
    window.clearInterval(this.#interval);
    this.#interval = undefined;
  }

  #advance() {
    const fromIndex = this.#index;
    const toIndex = (this.#index + 1) % this.#images.length;
    this.#index = toIndex;

    if (this.#gl && this.#canvas && this.#program) {
      this.#runTransition(fromIndex, toIndex);
      return;
    }

    for (const [i, image] of this.#images.entries()) {
      image.classList.toggle('is-active', i === toIndex);
    }
  }

  /**
   * @param {number} fromIndex
   * @param {number} toIndex
   */
  #runTransition(fromIndex, toIndex) {
    const gl = this.#gl;
    const program = this.#program;
    const canvas = this.#canvas;
    if (!gl || !program || !canvas) return;

    for (const [i, image] of this.#images.entries()) {
      image.classList.toggle('is-active', i === toIndex);
    }

    const durationMs = parseFloat(this.style.getPropertyValue('--password-hero-crossfade')) || 800;
    const startTime = performance.now();
    canvas.classList.add('is-visible');

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.#textures[fromIndex]);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.#textures[toIndex]);
    gl.uniform1i(this.#uniforms.textureA, 0);
    gl.uniform1i(this.#uniforms.textureB, 1);

    const step = () => {
      const progress = Math.min((performance.now() - startTime) / durationMs, 1);

      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform1f(this.#uniforms.progress, progress);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      if (progress >= 1) {
        canvas.classList.remove('is-visible');
        this.#rafId = undefined;
        return;
      }

      this.#rafId = requestAnimationFrame(step);
    };

    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(step);
  }

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#pause();
    } else {
      this.#play();
    }
  };
}

if (!customElements.get('password-hero-mobile-cycle')) {
  customElements.define('password-hero-mobile-cycle', PasswordHeroMobileCycle);
}

/* -------------------------------------------------------------------------
 * Entrance choreography: the hero image settles in (scale + fade), then the
 * logo and band fade/slide up staggered after it — see the stylesheet for
 * the actual timing, this just decides *when* to start it.
 * ---------------------------------------------------------------------- */

/**
 * Hidden by default via CSS (with a <noscript> override for no-JS visitors);
 * adds the class that reveals everything once the viewport-appropriate hero
 * image has loaded. A fallback timer reveals anyway if that image stalls, so
 * a slow connection never leaves the page blank indefinitely.
 */
class PasswordHeroReveal extends HTMLElement {
  static FALLBACK_TIMEOUT_MS = 4000;

  connectedCallback() {
    if (prefersReducedMotion()) {
      this.classList.add('is-revealed');
      return;
    }

    const image = this.#getRelevantImage();

    if (!image) {
      this.classList.add('is-revealed');
      return;
    }

    const timeout = window.setTimeout(() => this.classList.add('is-revealed'), PasswordHeroReveal.FALLBACK_TIMEOUT_MS);

    waitForImageReady(image)
      .then(() => this.classList.add('is-revealed'))
      .catch(() => this.classList.add('is-revealed'))
      .finally(() => window.clearTimeout(timeout));
  }

  #getRelevantImage() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const selector = isMobile ? '.password-hero__media--mobile img.is-active' : '.password-hero__media--desktop img';
    return this.querySelector(selector);
  }
}

if (!customElements.get('password-hero-reveal')) {
  customElements.define('password-hero-reveal', PasswordHeroReveal);
}
